import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  UserPlus,
  Mail,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users,
  Loader2,
  Send,
  Edit2,
  Info,
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';

interface PendingInvitation {
  id: string;
  name: string;
  role: string;
  email?: string;
  source: 'bdm_extraction' | 'manual';
  extractedAt: string;
  status: 'pending' | 'invited' | 'declined';
}

interface PendingStakeholderInvitationsProps {
  accessToken: string;
  companyId: string;
  companyName: string;
  userRole: string; // 'cf_admin' | 'cf_consultant' | 'company_admin'
  onInviteSent?: () => void;
}

export function PendingStakeholderInvitations({
  accessToken,
  companyId,
  companyName,
  userRole,
  onInviteSent,
}: PendingStakeholderInvitationsProps) {
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit dialog state
  const [editingInvitation, setEditingInvitation] = useState<PendingInvitation | null>(null);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editEmail, setEditEmail] = useState('');

  // Send invite dialog state
  const [sendingInvitation, setSendingInvitation] = useState<PendingInvitation | null>(null);
  const [sendEmail, setSendEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);

  // Bulk actions
  const [selectedInvitations, setSelectedInvitations] = useState<Set<string>>(new Set());
  const [bulkSending, setBulkSending] = useState(false);

  useEffect(() => {
    loadPendingInvitations();
  }, [companyId]);

  const loadPendingInvitations = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/pending-invitations`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPendingInvitations(data.invitations || []);
      }
    } catch (err) {
      console.error('Error loading pending invitations:', err);
      setError('Failed to load pending invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleEditInvitation = async () => {
    if (!editingInvitation) return;

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/pending-invitations/${editingInvitation.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: editName,
            role: editRole,
            email: editEmail || undefined,
          }),
        }
      );

      if (response.ok) {
        setSuccess('Stakeholder updated successfully');
        setEditingInvitation(null);
        loadPendingInvitations();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update stakeholder');
      }
    } catch (err) {
      console.error('Error updating invitation:', err);
      setError('Failed to update stakeholder');
    }
  };

  const handleDeleteInvitation = async (invitationId: string) => {
    if (!confirm('Are you sure you want to remove this stakeholder from pending invitations?')) {
      return;
    }

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/pending-invitations/${invitationId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        setSuccess('Stakeholder removed from pending list');
        loadPendingInvitations();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to remove stakeholder');
      }
    } catch (err) {
      console.error('Error deleting invitation:', err);
      setError('Failed to remove stakeholder');
    }
  };

  const handleSendInvite = async () => {
    if (!sendingInvitation || !sendEmail) return;

    try {
      setSendingInvite(true);
      setError('');

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/pending-invitations/${sendingInvitation.id}/send`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: sendEmail,
          }),
        }
      );

      if (response.ok) {
        setSuccess(`Invitation sent to ${sendEmail}`);
        setSendingInvitation(null);
        setSendEmail('');
        loadPendingInvitations();
        onInviteSent?.();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to send invitation');
      }
    } catch (err) {
      console.error('Error sending invitation:', err);
      setError('Failed to send invitation');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleBulkSend = async () => {
    if (selectedInvitations.size === 0) return;

    // Check if all selected have emails
    const selected = pendingInvitations.filter(inv => 
      selectedInvitations.has(inv.id) && inv.status === 'pending'
    );
    const missingEmails = selected.filter(inv => !inv.email);

    if (missingEmails.length > 0) {
      setError(`${missingEmails.length} stakeholder(s) are missing email addresses. Please add emails before sending.`);
      return;
    }

    if (!confirm(`Send invitations to ${selected.length} stakeholder(s)?`)) {
      return;
    }

    try {
      setBulkSending(true);
      setError('');

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/pending-invitations/bulk-send`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            invitationIds: Array.from(selectedInvitations),
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSuccess(`Successfully sent ${data.sent} invitation(s)`);
        setSelectedInvitations(new Set());
        loadPendingInvitations();
        onInviteSent?.();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to send invitations');
      }
    } catch (err) {
      console.error('Error sending bulk invitations:', err);
      setError('Failed to send invitations');
    } finally {
      setBulkSending(false);
    }
  };

  const toggleSelection = (invitationId: string) => {
    const newSelection = new Set(selectedInvitations);
    if (newSelection.has(invitationId)) {
      newSelection.delete(invitationId);
    } else {
      newSelection.add(invitationId);
    }
    setSelectedInvitations(newSelection);
  };

  const selectAll = () => {
    const pendingOnly = pendingInvitations.filter(inv => inv.status === 'pending');
    setSelectedInvitations(new Set(pendingOnly.map(inv => inv.id)));
  };

  const deselectAll = () => {
    setSelectedInvitations(new Set());
  };

  const pendingCount = pendingInvitations.filter(inv => inv.status === 'pending').length;
  const invitedCount = pendingInvitations.filter(inv => inv.status === 'invited').length;

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          Loading pending invitations...
        </CardContent>
      </Card>
    );
  }

  if (pendingInvitations.length === 0) {
    return null; // Don't show anything if there are no pending invitations
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Pending Stakeholder Invitations
              </CardTitle>
              <CardDescription>
                Stakeholders identified during discovery - ready to invite
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                {pendingCount} pending
              </Badge>
              {invitedCount > 0 && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  {invitedCount} invited
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="bg-green-50 text-green-900 border-green-200">
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {pendingCount > 0 && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                These stakeholders were identified from BDM discovery notes. Review and add email addresses, then send invitations.
              </AlertDescription>
            </Alert>
          )}

          {/* Bulk Actions */}
          {pendingCount > 0 && (
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={selectedInvitations.size === pendingCount ? deselectAll : selectAll}
                >
                  {selectedInvitations.size === pendingCount ? 'Deselect All' : 'Select All'}
                </Button>
                {selectedInvitations.size > 0 && (
                  <span className="text-sm text-muted-foreground">
                    {selectedInvitations.size} selected
                  </span>
                )}
              </div>
              {selectedInvitations.size > 0 && (
                <Button
                  onClick={handleBulkSend}
                  disabled={bulkSending}
                  size="sm"
                >
                  {bulkSending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send {selectedInvitations.size} Invitation{selectedInvitations.size > 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              )}
            </div>
          )}

          {/* Invitations List */}
          <div className="space-y-2">
            {pendingInvitations.map((invitation) => (
              <div
                key={invitation.id}
                className={`p-4 border rounded-lg transition-colors ${
                  invitation.status === 'invited'
                    ? 'bg-green-50/50 border-green-200'
                    : invitation.status === 'declined'
                    ? 'bg-gray-50 border-gray-200 opacity-50'
                    : selectedInvitations.has(invitation.id)
                    ? 'bg-primary/5 border-primary'
                    : 'bg-background'
                }`}
              >
                <div className="flex items-start gap-3">
                  {invitation.status === 'pending' && (
                    <input
                      type="checkbox"
                      checked={selectedInvitations.has(invitation.id)}
                      onChange={() => toggleSelection(invitation.id)}
                      className="mt-1 cursor-pointer"
                    />
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium">{invitation.name}</h4>
                          {invitation.status === 'invited' && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Invited
                            </Badge>
                          )}
                          {invitation.status === 'declined' && (
                            <Badge variant="outline" className="bg-gray-50 text-gray-600">
                              <XCircle className="h-3 w-3 mr-1" />
                              Declined
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-1">{invitation.role}</p>
                        {invitation.email ? (
                          <div className="flex items-center gap-1 text-sm">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            <span>{invitation.email}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-sm text-amber-600">
                            <AlertCircle className="h-3 w-3" />
                            <span>No email address</span>
                          </div>
                        )}
                      </div>

                      {invitation.status === 'pending' && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingInvitation(invitation);
                              setEditName(invitation.name);
                              setEditRole(invitation.role);
                              setEditEmail(invitation.email || '');
                            }}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          {invitation.email && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => {
                                setSendingInvitation(invitation);
                                setSendEmail(invitation.email || '');
                              }}
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Send Invite
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteInvitation(invitation.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingInvitation} onOpenChange={(open) => !open && setEditingInvitation(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Stakeholder</DialogTitle>
            <DialogDescription>
              Update stakeholder information before sending invitation
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="e.g., Mike Chen"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-role">Role *</Label>
              <Input
                id="edit-role"
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
                placeholder="e.g., IT Director"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-email">Email Address</Label>
              <Input
                id="edit-email"
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                placeholder="email@example.com"
              />
              <p className="text-xs text-muted-foreground">
                Required before sending invitation
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingInvitation(null)}>
              Cancel
            </Button>
            <Button onClick={handleEditInvitation} disabled={!editName || !editRole}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Invite Dialog */}
      <Dialog open={!!sendingInvitation} onOpenChange={(open) => !open && setSendingInvitation(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Invitation</DialogTitle>
            <DialogDescription>
              Send an invitation to {sendingInvitation?.name} to join {companyName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm">
                <span className="font-medium">{sendingInvitation?.name}</span>
                <br />
                <span className="text-muted-foreground">{sendingInvitation?.role}</span>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="send-email">Email Address *</Label>
              <Input
                id="send-email"
                type="email"
                value={sendEmail}
                onChange={(e) => setSendEmail(e.target.value)}
                placeholder="email@example.com"
              />
            </div>

            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                An invitation email will be sent with instructions to create their account.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSendingInvitation(null)} disabled={sendingInvite}>
              Cancel
            </Button>
            <Button onClick={handleSendInvite} disabled={!sendEmail || sendingInvite}>
              {sendingInvite ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
